import { useEffect, useState } from "react";
import "./App.css";

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatEstimatedTime(minutes) {
  if (!minutes) {
    return "No estimate";
  }

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (!remainingMinutes) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
}

function reorderThings(things, draggedId, targetId) {
  const draggedIndex = things.findIndex((thing) => thing.id === draggedId);
  const targetIndex = things.findIndex((thing) => thing.id === targetId);

  if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
    return things;
  }

  const nextThings = [...things];
  const [draggedThing] = nextThings.splice(draggedIndex, 1);
  nextThings.splice(targetIndex, 0, draggedThing);

  return nextThings.map((thing, index) => ({
    ...thing,
    priority: index + 1,
  }));
}

function App() {
  const [user, setUser] = useState(null);
  const [things, setThings] = useState([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
    estimatedMinutes: "",
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [sessionLoading, setSessionLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [draggedThingId, setDraggedThingId] = useState(null);
  const [dragTargetThingId, setDragTargetThingId] = useState(null);
  const [justDroppedId, setJustDroppedId] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [error, setError] = useState("");
  const [authError, setAuthError] = useState("");
  const topThing = things[0];
  const estimatedThings = things.filter((thing) => Boolean(thing.estimated_minutes));
  const totalEstimatedMinutes = estimatedThings.reduce(
    (total, thing) => total + thing.estimated_minutes,
    0
  );

  function handleUnauthorized(message) {
    setUser(null);
    setThings([]);
    setError("");
    setAuthError(message);
  }

  async function loadThings() {
    try {
      setLoading(true);
      setError("");

      const response = await fetch("/api/things");

      if (response.status === 401) {
        handleUnauthorized("Your session expired. Please log in again.");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch things");
      }

      const data = await response.json();
      setThings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function checkSession() {
      try {
        setSessionLoading(true);
        setAuthError("");

        const response = await fetch("/api/session");

        if (response.status === 401) {
          setUser(null);
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to verify session");
        }

        const data = await response.json();
        setUser(data.user);

        setLoading(true);

        const thingsResponse = await fetch("/api/things");

        if (thingsResponse.status === 401) {
          handleUnauthorized("Your session expired. Please log in again.");
          return;
        }

        if (!thingsResponse.ok) {
          throw new Error("Failed to fetch things");
        }

        const thingsData = await thingsResponse.json();
        setThings(thingsData);
      } catch (err) {
        setAuthError(err.message);
      } finally {
        setLoading(false);
        setSessionLoading(false);
      }
    }

    checkSession();
  }, []);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleLoginChange(event) {
    const { name, value } = event.target;
    setLoginForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  async function handleLogin(event) {
    event.preventDefault();

    try {
      setLoggingIn(true);
      setAuthError("");

      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginForm),
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || "Login failed");
      }

      setUser(payload.user);
      setLoginForm({ email: "", password: "" });
      await loadThings();
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setLoggingIn(false);
      setSessionLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await fetch("/api/logout", { method: "POST" });
    } finally {
      setUser(null);
      setThings([]);
      setForm({ name: "", description: "", estimatedMinutes: "" });
      setDraggedThingId(null);
      setDragTargetThingId(null);
      setError("");
      setAuthError("");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSubmitting(true);
      setError("");

      const response = await fetch("/api/things", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      if (response.status === 401) {
        handleUnauthorized("Your session expired. Please log in again.");
        return;
      }

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || "Failed to create thing");
      }

      setThings((current) => [...current, payload.thing]);
      setForm({ name: "", description: "", estimatedMinutes: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function persistThingOrder(nextThings) {
    const response = await fetch("/api/things/reorder", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        orderedIds: nextThings.map((thing) => thing.id),
      }),
    });

    if (response.status === 401) {
      handleUnauthorized("Your session expired. Please log in again.");
      return { unauthorized: true };
    }

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload.message || "Failed to save priorities");
    }

    return { unauthorized: false };
  }

  function handleDragStart(thingId) {
    if (reordering) {
      return;
    }

    setError("");
    setDraggedThingId(thingId);
    setDragTargetThingId(thingId);
  }

  function handleDragEnd() {
    setDraggedThingId(null);
    setDragTargetThingId(null);
  }

  async function handleDrop(targetThingId) {
    if (!draggedThingId || draggedThingId === targetThingId || reordering) {
      handleDragEnd();
      return;
    }

    const previousThings = things;
    const reorderedThings = reorderThings(things, draggedThingId, targetThingId);

    if (reorderedThings === things) {
      handleDragEnd();
      return;
    }

    const droppedId = draggedThingId;
    setThings(reorderedThings);
    setReordering(true);
    setJustDroppedId(droppedId);
    handleDragEnd();

    try {
      const result = await persistThingOrder(reorderedThings);

      if (result.unauthorized) {
        return;
      }
    } catch (err) {
      setThings(previousThings);
      setError(err.message);
    } finally {
      setReordering(false);
      setTimeout(() => setJustDroppedId(null), 500);
    }
  }

  async function handleComplete(thingId) {
    setCompleting(thingId);
    try {
      const response = await fetch(`/api/things/${thingId}/complete`, {
        method: "PATCH",
      });

      if (response.status === 401) {
        handleUnauthorized("Your session expired. Please log in again.");
        return;
      }

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.message || "Failed to mark as done");
      }

      setThings((current) => current.filter((t) => t.id !== thingId));
    } catch (err) {
      setError(err.message);
    } finally {
      setCompleting(null);
    }
  }

  if (sessionLoading) {
    return (
      <main className="app-shell auth-shell">
        <div className="app-glow glow-left" />
        <div className="app-glow glow-right" />
        <section className="auth-card">
          <p className="eyebrow">Household Things</p>
          <span className="status-pill status-pill-neutral">Loading</span>
          <h1>Checking your session...</h1>
          <p className="intro">
            One moment while we load your shared priorities.
          </p>
          <div className="loading-bar" aria-hidden="true" />
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="app-shell auth-shell">
        <div className="app-glow glow-left" />
        <div className="app-glow glow-right" />

        <div className="auth-layout">
          <section className="auth-card auth-card-feature">
            <p className="eyebrow">Shared Home Board</p>
            <span className="status-pill">Priority based</span>
            <h1>Keep life and house tasks in one simple ranked list.</h1>
            <p className="intro">
              Add a Thing, drag it higher when it becomes urgent, and keep your
              next priorities visible at a glance.
            </p>

            <div className="feature-grid">
              <article className="feature-tile">
                <strong>One list</strong>
                <span>No extra categories. Just the Things that matter next.</span>
              </article>
              <article className="feature-tile">
                <strong>Drag to rank</strong>
                <span>The top card is priority 1, and order is saved to the backend.</span>
              </article>
              <article className="feature-tile">
                <strong>Time aware</strong>
                <span>Add estimated minutes so you can spot the quick wins fast.</span>
              </article>
            </div>
          </section>

          <section className="auth-card auth-card-form">
            <p className="eyebrow">Member Login</p>
            <h2>Authorized users only</h2>
            <p className="intro">
              Sign in to manage the shared Things list for your home and life.
            </p>

            <form className="thing-form" onSubmit={handleLogin}>
              <label>
                Email
                <input
                  type="email"
                  name="email"
                  value={loginForm.email}
                  onChange={handleLoginChange}
                  placeholder="name@example.com"
                  required
                />
              </label>

              <label>
                Password
                <input
                  type="password"
                  name="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  placeholder="Enter your password"
                  required
                />
              </label>

              {authError ? <p className="status error">{authError}</p> : null}

              <button type="submit" disabled={loggingIn}>
                {loggingIn ? "Signing in..." : "Log in"}
              </button>
            </form>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <div className="app-glow glow-left" />
      <div className="app-glow glow-right" />

      <section className="hero panel hero-panel">
        <div className="hero-copy">
          <p className="eyebrow">Shared Things</p>
          <div className="hero-badges">
            <span className="status-pill">Priority list</span>
            <span className="status-pill status-pill-neutral">Drag to reorder</span>
          </div>
          <h1>Keep the next thing to do at the very top.</h1>
          <p className="intro">
            Signed in as <strong>{user.email}</strong>. Add Things for your
            house or life, then drag cards to rank them. The top card is priority 1.
          </p>
        </div>

        <div className="hero-side">
          <button className="logout-button" type="button" onClick={handleLogout}>
            Log out
          </button>

          <div className="spotlight-card">
            <span className="spotlight-label">Top priority</span>
            <strong>{topThing ? topThing.name : "Nothing on deck yet"}</strong>
            <p>
              {topThing
                ? `${formatEstimatedTime(topThing.estimated_minutes)} - created ${formatTimestamp(
                    topThing.created_at
                  )}`
                : "Create your first Thing and drag it into the right spot."}
            </p>
          </div>
        </div>

        <div className="stats-grid">
          <article className="stat-card">
            <span>Total Things</span>
            <strong>{things.length}</strong>
            <p>Everything currently on your shared priorities list.</p>
          </article>
          <article className="stat-card">
            <span>Estimated time</span>
            <strong>
              {estimatedThings.length ? formatEstimatedTime(totalEstimatedMinutes) : "Add time"}
            </strong>
            <p>
              {estimatedThings.length
                ? `${estimatedThings.length} Things currently have a time estimate.`
                : "Start adding minutes to compare quick wins against larger jobs."}
            </p>
          </article>
          <article className="stat-card">
            <span>How to prioritize</span>
            <strong>{reordering ? "Saving..." : "Drag cards"}</strong>
            <p>Drop a Thing above or below the others and we save the new order.</p>
          </article>
        </div>
      </section>

      <div className="dashboard-grid">
        <section className="panel panel-form">
          <div className="panel-heading">
            <p className="eyebrow">New Thing</p>
            <h2>Add something that needs to get done</h2>
            <p>Keep it simple: name it, add optional notes, and estimate the time.</p>
          </div>

          <form className="thing-form" onSubmit={handleSubmit}>
            <label>
              Thing
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Book dentist appointment"
                required
              />
            </label>

            <label>
              Notes
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Optional details, reminders, or context"
                rows="5"
              />
            </label>

            <label>
              Estimated time in minutes
              <input
                type="number"
                min="1"
                step="1"
                name="estimatedMinutes"
                value={form.estimatedMinutes}
                onChange={handleChange}
                placeholder="45"
              />
            </label>

            <button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Add Thing"}
            </button>
          </form>
        </section>

        <section className="panel panel-list">
          <div className="panel-heading">
            <p className="eyebrow">Prioritized List</p>
            <h2>Things ranked from most important to least important</h2>
            <p>Drag any card to a new spot. Priority updates automatically from the top down.</p>
          </div>

          {error ? <p className="status error">{error}</p> : null}
          {loading ? <p className="status">Loading Things...</p> : null}
          {reordering ? <p className="status">Saving the new priority order...</p> : null}

          {!loading && !things.length ? (
            <div className="empty-state">
              <strong>No Things yet</strong>
              <p>Add the first one from the form, then drag it into place as priorities change.</p>
            </div>
          ) : null}

          <ol className="thing-list">
            {things.map((thing, index) => {
              const isDragging = draggedThingId === thing.id;
              const isDropTarget =
                dragTargetThingId === thing.id && draggedThingId !== thing.id;
              const isJustDropped = justDroppedId === thing.id;
              const isCompleting = completing === thing.id;

              return (
                <li className="thing-list-item" key={thing.id}>
                  <article
                    className={[
                      "thing-card",
                      isDragging ? "is-dragging" : "",
                      isDropTarget ? "is-drop-target" : "",
                      isJustDropped ? "just-dropped" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    draggable={!reordering && !isCompleting}
                    onDragStart={() => handleDragStart(thing.id)}
                    onDragEnter={() => setDragTargetThingId(thing.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(thing.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="thing-card-topline">
                      <span className="thing-badge">Priority {index + 1}</span>
                      <span className="thing-time">
                        {formatEstimatedTime(thing.estimated_minutes)}
                      </span>
                    </div>

                    <h3>{thing.name}</h3>
                    <p>{thing.description || "No notes yet."}</p>

                    <div className="thing-card-footer">
                      <span>Created {formatTimestamp(thing.created_at)}</span>
                      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <button
                          className="complete-button"
                          type="button"
                          disabled={isCompleting || reordering}
                          onClick={() => handleComplete(thing.id)}
                        >
                          {isCompleting ? "Marking done..." : "✓ Mark done"}
                        </button>
                        <span className="drag-hint">Drag to reprioritize</span>
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </main>
  );
}

export default App;
